import { useEffect, useMemo, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'
import type { TFunction } from 'i18next'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'

import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  DarkProvider,
  IconButton,
  ShareIcon,
  StatusPill,
  Tooltip,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { IncidentQueryResult } from '@/graphql/generated/hooks'
import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { formatDuration } from '../shared/utils/formatDuration'
import { getOrigin, getStatusVariant } from '../shared/utils/helpers'

type IncidentData = IncidentQueryResult['data']['incident']

interface DarkHeaderIProps {
  incident: IncidentData
  isLoading: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  canNavigatePrevious?: boolean
  canNavigateNext?: boolean
  showNavigation?: boolean
  colorScheme?: ColorScheme
  deviceIsUnhealthy?: boolean
}

function getLatestAcknowledgementTime(incident: IncidentData): string | null {
  if (!incident?.actions) {
    return null
  }

  const actions = incident.actions.filter((a): a is NonNullable<typeof a> =>
    Boolean(a)
  )

  const acknowledgementActions = actions.filter(
    (action) =>
      action.type === IncidentActionType.AcknowledgementByOperator ||
      action.type === IncidentActionType.AcknowledgementByGuard
  )

  if (acknowledgementActions.length === 0) {
    return null
  }

  const sorted = [...acknowledgementActions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  return sorted[0].createdAt
}

function useElapsedTime(startTime: string | null): number {
  const [elapsed, setElapsed] = useState(() => {
    if (!startTime) return 0
    return Date.now() - new Date(startTime).getTime()
  })

  useEffect(() => {
    if (!startTime) {
      // The header stays mounted across incident navigation; without the
      // reset a null startTime keeps the previous incident's elapsed time.
      setElapsed(0)
      return
    }

    const updateElapsed = () => {
      setElapsed(Date.now() - new Date(startTime).getTime())
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  return elapsed
}

function getStatusLabel(
  status: IncidentStatus,
  activeElapsed: number,
  inProgressElapsed: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>
): string {
  switch (status) {
    case IncidentStatus.Active:
      return tVar(t, 'incidents:incident_drawer_status_active', {
        duration: formatDuration(activeElapsed),
      })
    case IncidentStatus.InProgress:
      return tVar(t, 'incidents:incident_drawer_status_in_progress', {
        duration: formatDuration(inProgressElapsed),
      })
    case IncidentStatus.Resolved:
      return t('incidents:incident_drawer_status_resolved')
    default:
      return ''
  }
}

export const DarkHeader = ({
  incident,
  isLoading,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  canNavigatePrevious = false,
  canNavigateNext = false,
  showNavigation = false,
  colorScheme = 'dark',
  deviceIsUnhealthy = false,
}: DarkHeaderIProps) => {
  const { t } = useTranslation()
  const origin = getOrigin()
  const { query } = useRouter()
  const displayId = incident?.displayId ?? (query?.displayId as string)
  const incidentLink =
    origin && displayId ? `${origin}/incidents/?displayId=${displayId}` : ''

  const status = incident?.status

  const activeStartTime =
    status === IncidentStatus.Active ? incident?.createdAt : null
  const inProgressStartTime = useMemo(
    () =>
      status === IncidentStatus.InProgress
        ? getLatestAcknowledgementTime(incident)
        : null,
    [status, incident?.actions]
  )

  const activeElapsed = useElapsedTime(activeStartTime)
  const inProgressElapsed = useElapsedTime(inProgressStartTime)

  const [hasCopied, setHasCopied] = useState(false)
  const [isHoveringCopy, setIsHoveringCopy] = useState(false)
  const [isHoveringUnhealthy, setIsHoveringUnhealthy] = useState(false)
  const shareIconRef = useRef<HTMLDivElement>(null)
  const unhealthyPillRef = useRef<HTMLDivElement>(null)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!deviceIsUnhealthy) setIsHoveringUnhealthy(false)
  }, [deviceIsUnhealthy])

  const handleCopyLink = async () => {
    if (!incidentLink || !navigator?.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(incidentLink)
      setHasCopied(true)
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
      resetTimerRef.current = setTimeout(() => setHasCopied(false), 3000)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const prefix = colorScheme === 'light' ? 'light.' : ''

  return (
    <DarkProvider>
      <Box
        alignItems='center'
        borderBottom='1px solid'
        borderColor={`${prefix}interactive.tertiary-border`}
        display='flex'
        height='64px'
        justifyContent='space-between'
        px='24px'
        py='0'
        width='100%'
      >
        {/* region Left section */}
        <Box
          alignItems='center'
          display='flex'
          flex='1'
          gap='8px'
          overflow='hidden'
        >
          {/* Close button */}
          <IconButton
            _active={{ bg: 'transparent' }}
            aria-label='close incident drawer'
            colorScheme={colorScheme}
            dimension={36}
            hoverBgColor='transparent'
            icon={<CloseIcon size={11} />}
            onClick={onClose}
          />

          {/* Pager navigation */}
          {showNavigation && (
            <Box
              alignItems='center'
              border='1px solid'
              borderColor={`${prefix}border.subtle`}
              display='flex'
              gap='0'
              marginLeft='8px'
            >
              <IconButton
                aria-label='previous incident'
                colorScheme={colorScheme}
                data-testid='incidentDrawer_header_previousIncidentButton'
                dimension={24}
                disabled={!canNavigatePrevious}
                icon={
                  <ChevronUpIcon
                    color={`var(--hw-dark-colors-${
                      colorScheme === 'light' ? 'light-' : ''
                    }icon-secondary)`}
                  />
                }
                onClick={onNavigatePrevious}
                style={{ opacity: canNavigatePrevious ? 1 : 0.4 }}
              />
              <IconButton
                aria-label='next incident'
                colorScheme={colorScheme}
                data-testid='incidentDrawer_header_nextIncidentButton'
                dimension={24}
                disabled={!canNavigateNext}
                icon={
                  <ChevronDownIcon
                    color={`var(--hw-dark-colors-${
                      colorScheme === 'light' ? 'light-' : ''
                    }icon-secondary)`}
                  />
                }
                onClick={onNavigateNext}
                style={{ opacity: canNavigateNext ? 1 : 0.4 }}
              />
            </Box>
          )}

          {/* Incident name */}
          {!isLoading && incident?.name && (
            <Box
              color={`${prefix}text.primary`}
              data-testid='incidentDrawer_header_incidentName'
              flexShrink={0}
              fontFamily="'Soehne Kraftig', sans-serif"
              fontSize='16px'
              fontWeight='500'
              lineHeight='24px'
              maxWidth='175px'
              overflow='hidden'
              textOverflow='ellipsis'
              whiteSpace='nowrap'
            >
              {incident.name}
            </Box>
          )}

          {/* Divider */}
          <Box
            bg={`${prefix}border.subtle`}
            flexShrink={0}
            height='20px'
            width='1px'
          />

          {/* Display ID + copy link */}
          {!isLoading && displayId && (
            <Box
              _hover={{ bg: `${prefix}interactive.icon-button-hover-bg` }}
              alignItems='center'
              cursor='pointer'
              display='flex'
              gap='8px'
              onClick={handleCopyLink}
              onMouseEnter={() => setIsHoveringCopy(true)}
              onMouseLeave={() => setIsHoveringCopy(false)}
              px='8px'
              py='4px'
              ref={shareIconRef}
            >
              <Box
                color={`${prefix}text.secondary`}
                data-testid='incidentDrawer_header_incidentId'
                fontFamily="'Soehne Mono', monospace"
                fontSize='12px'
                letterSpacing='1px'
                lineHeight='20px'
                textTransform='uppercase'
                whiteSpace='nowrap'
              >
                {displayId}
              </Box>
              <Box
                alignItems='center'
                color={
                  hasCopied
                    ? `${prefix}status.resolved-text`
                    : `${prefix}icon.secondary`
                }
                display='flex'
                flexShrink={0}
              >
                <ShareIcon color='currentColor' />
              </Box>
              {(isHoveringCopy || hasCopied) && (
                <Tooltip
                  anchorEl={shareIconRef.current}
                  colorScheme={colorScheme}
                  label={
                    hasCopied
                      ? t('common:copied')
                      : t('incidents:drawer_copy_link_aria')
                  }
                  placement='bottom'
                />
              )}
            </Box>
          )}

          {/* Status pill */}
          {status && (
            <StatusPill
              colorScheme={colorScheme}
              label={getStatusLabel(
                status,
                activeElapsed,
                inProgressElapsed,
                t
              )}
              variant={getStatusVariant(status)}
            />
          )}

          {/* Unhealthy pill — marginLeft auto pushes it to the right */}
          {deviceIsUnhealthy && (
            <Box
              flexShrink={0}
              marginLeft='auto'
              onMouseEnter={() => setIsHoveringUnhealthy(true)}
              onMouseLeave={() => setIsHoveringUnhealthy(false)}
              ref={unhealthyPillRef}
            >
              <StatusPill
                colorScheme={colorScheme}
                label={t('incidents:drawer_device_unhealthy_label')}
                variant='error'
              />
              {isHoveringUnhealthy && (
                <Tooltip
                  anchorEl={unhealthyPillRef.current}
                  colorScheme={colorScheme}
                  label={t('incidents:drawer_device_unhealthy_tooltip')}
                  placement='bottom'
                />
              )}
            </Box>
          )}
        </Box>
        {/* endregion */}
      </Box>
    </DarkProvider>
  )
}
